// Walks an arbitrary scene payload and tallies inline `data:` URIs by their
// field path. Used by error reporting to answer "why is this scene 47 MB"
// without forcing the user to inspect the raw payload by hand.
//
// Returns:
//   {
//     totalBytes: number,                       // sum of all data: URI byte sizes
//     payloadBytes: number,                     // total JSON-stringified size
//     blobs: [{ path: "uploadImages.bg.main", bytes: 15873421 }, ...],
//   }
//
// "bytes" is the decoded base64 size (i.e. what the actual binary would be
// once decoded), not the inflated base64 string length — that's the number
// users intuitively map to "image size."

const DATA_URI_PREFIX = "data:";

// base64 inflates by ~4/3 plus padding. Compute decoded size from the
// base64 portion length without actually decoding.
function decodedSizeFromBase64Length(base64Length) {
  // Each 4 base64 chars encode 3 bytes; trailing '=' pads reduce by 1 each.
  // We don't have the actual string here, so this is a tight upper bound.
  return Math.floor((base64Length * 3) / 4);
}

function parseDataUri(value) {
  if (typeof value !== "string" || !value.startsWith(DATA_URI_PREFIX)) return null;
  const commaIdx = value.indexOf(",");
  if (commaIdx === -1) return null;
  const header = value.slice(DATA_URI_PREFIX.length, commaIdx); // e.g. "image/png;base64"
  const mediatype = header.split(";")[0] || "";
  const isBase64 = header.includes("base64");
  const payload = value.slice(commaIdx + 1);
  const bytes = isBase64 ? decodedSizeFromBase64Length(payload.length) : payload.length;
  return {bytes, mediatype};
}

export function introspectScenePayload(payload) {
  const blobs = [];

  function walk(value, path) {
    if (value === null || value === undefined) return;
    if (typeof value === "string") {
      const parsed = parseDataUri(value);
      if (parsed && parsed.bytes > 0) {
        blobs.push({path, bytes: parsed.bytes, mediatype: parsed.mediatype});
      }
      return;
    }
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) walk(value[i], `${path}[${i}]`);
      return;
    }
    if (typeof value === "object") {
      for (const key of Object.keys(value)) {
        walk(value[key], path ? `${path}.${key}` : key);
      }
    }
  }

  walk(payload, "");

  blobs.sort((a, b) => b.bytes - a.bytes);
  const totalBytes = blobs.reduce((sum, b) => sum + b.bytes, 0);

  // payloadBytes uses Blob to count the actual JSON-serialized byte length,
  // which matters because base64 inflates UTF-8 string lengths in the wire
  // payload. This is the number Kraken sees, not the decoded-blob total.
  let payloadBytes = 0;
  try {
    payloadBytes = new Blob([JSON.stringify(payload)]).size;
  } catch {
    // Fallback for environments without Blob (or circular refs): leave 0.
  }

  return {totalBytes, payloadBytes, blobs};
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
